import { TSelectedCompany } from "./../../store";
import axiosInstance from "../../axios";
import {
  ForceUpdateItem,
  TTopDevicesRequest,
  TUsersByEldParams,
} from "./types";
import moment from "moment";

export const statistics = {
  list: async (
    startDate: string,
    endDate: string,
    selectedCompany: string,
    statisticType = "",
    companyIds?: string[],
  ) => {
    try {
      const response = await axiosInstance.get(
        companyIds?.length
          ? `/statistics/all-statistics-by-eld-and-companies`
          : `/statistics/all-statistics-by-eld`,
        {
          params: {
            startDate,
            endDate,
            eldType: selectedCompany,
            statisticType,
            companyIds,
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("List error:", error);
      throw error;
    }
  },
  companiesList: async ({
    selectedCompany,
    page = 1,
    perPage = 100,
    searchName,
  }: {
    selectedCompany: string;
    page?: number;
    perPage?: number;
    searchName?: string;
  }) => {
    try {
      const response = await axiosInstance.get(
        `/companies/list-for-statistic`,
        {
          params: {
            page,
            perPage,
            searchName,
            eldType: selectedCompany,
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("List error:", error);
      throw error;
    }
  },
  drivers: async ({
    page = 1,
    perPage = 100,
    searchName,
    eldType,
    companyIds,
  }: {
    page?: number;
    perPage?: number;
    searchName?: string;
    eldType: TSelectedCompany;
    companyIds?: number[];
  }) => {
    try {
      const response = await axiosInstance.get(`/users/list-users-by-eld`, {
        params: {
          page,
          perPage,
          searchName,
          role: "driver",
          eldType,
          companyIds,
        },
      });

      return response.data;
    } catch (error) {
      console.error("List error:", error);
      throw error;
    }
  },
  financialMetrics: async (
    selectedCompany: string,
    isActive: boolean = false,
  ) => {
    try {
      const response = await axiosInstance.get(`/statistics/today-by-eld`, {
        params: {
          eldType: selectedCompany,
          isActive,
        },
      });

      return response.data;
    } catch (error) {
      console.error("List error:", error);
      throw error;
    }
  },
  topDevices: async (
    request: TTopDevicesRequest,
  ): Promise<{ data: string; count: number }> => {
    try {
      const response = await axiosInstance.get(
        `/statistics/top-devices-by-eld`,
        {
          params: request,
        },
      );

      return response.data;
    } catch (error) {
      console.error("List error:", error);
      throw error;
    }
  },
  getTruckLimit: async (eldType: string) => {
    try {
      const response = await axiosInstance.get(`/platforms/find-by-eld`, {
        params: {
          eldType,
        },
      });

      return response.data;
    } catch (error) {
      console.error("List error:", error);
      throw error;
    }
  },
  setTruckLimit: async (eldType: string, quantity: number) => {
    try {
      const response = await axiosInstance.patch(
        `/platforms/edit-by-eld`,
        { quantity: +quantity },
        {
          params: {
            eldType,
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("List error:", error);
      throw error;
    }
  },
  setDueDate: async (eldType: string, quantity: number) => {
    try {
      const response = await axiosInstance.patch(
        `/platforms/edit-by-eld`,
        { paymentDueDay: +quantity },
        {
          params: {
            eldType,
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("List error:", error);
      throw error;
    }
  },
  editUsersByEld: async (
    data: {
      isAllDrivers: boolean;
      companyIds: number[];
      userIds: number[];
      latestAndroidVersionCode?: number;
      latestIOSVersionCode?: number;
    },
    eldType: TSelectedCompany,
  ) => {
    return await axiosInstance.patch("users/edit-users-by-eld", data, {
      params: { eldType },
    });
  },
  getUsersByEld: async (params: TUsersByEldParams) => {
    const response = await axiosInstance.get("/request/list-by-eld", {
      params,
    });
    const mappedItems = response.data.data.map(
      (item: any): ForceUpdateItem => ({
        id: item.id,
        androidVersion: item.requestData?.androidVersionCode ?? 0,
        iosVersion: item.requestData?.iosVersionCode ?? 0,
        companiesCount: item.companyCount,
        userCount: item.userCount,
        createdBy: `${item.creator?.first_name ?? ""} ${item.creator?.second_name ?? ""}`,
        createdAt: item.createdAt,
      }),
    );
    return {
      count: response.data.count,
      data: mappedItems,
    };
  },
  getCompanyByEld: async (
    eldType: string,
    isActive: boolean,
    model: "driver" | "vehicle",
  ) => {
    try {
      const { data }: any = await axiosInstance.get(
        `/statistics/company-by-eld`,
        {
          params: {
            eldType,
            model,
            isActive,
          },
        },
      );

      return data;
    } catch (error) {
      console.error("List error:", error);
      throw error;
    }
  },
  getDriversAndVehiclesById: async (id: string, eldType: string) => {
    try {
      const response = await axiosInstance.get(`/request/find-by-eld/${id}`, {
        params: { eldType },
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },
};
